import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOrganizationContext } from '@/contexts/OrganizationContext';
import { useAuth } from '@/hooks/useAuth';

interface UseProductSelectionReturn {
  needsProductSelection: boolean;
  isLoading: boolean;
  selectedProducts: string[];
}

/**
 * Hook para verificar se a organização precisa escolher produtos
 */
export function useProductSelection(): UseProductSelectionReturn {
  const { currentOrg, loading: orgLoading } = useOrganizationContext();
  const { user } = useAuth();
  const [needsProductSelection, setNeedsProductSelection] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);

  useEffect(() => {
    const checkProductSelection = async () => {
      // Se não há usuário autenticado, não precisa verificar
      if (!user) {
        setNeedsProductSelection(false);
        setIsLoading(false);
        return;
      }

      // Aguardar carregamento das organizações
      if (orgLoading) {
        return;
      }

      // IMPORTANTE: A página de seleção de produtos só deve aparecer DEPOIS da criação da primeira organização
      // Se não há organização, não mostrar a página de seleção ainda - o usuário precisa criar primeiro
      if (!currentOrg?.id) {
        setNeedsProductSelection(false);
        setIsLoading(false);
        return;
      }

      // Só verificar produtos se já houver uma organização criada
      setIsLoading(true);

      try {
        const { data, error } = await supabase
          .from('organization_products')
          .select('product_key')
          .eq('org_id', currentOrg.id);

        if (error) {
          // Se a tabela não existe (404) ou erro de permissão, considerar que precisa selecionar
          if (error.code === '42P01' || error.code === 'PGRST116' || error.message.includes('does not exist') || error.message.includes('404')) {
            // Tabela não existe ou erro 404 - precisa selecionar produtos
            setNeedsProductSelection(true);
          } else {
            console.error('Erro ao verificar produtos:', error);
            // Em caso de erro desconhecido, assumir que precisa selecionar (mais seguro)
            setNeedsProductSelection(true);
          }
          setIsLoading(false);
          return;
        }

        if (!data || data.length === 0) {
          // Não tem produtos selecionados - precisa selecionar
          setNeedsProductSelection(true);
        } else {
          // Tem produtos selecionados - não precisa selecionar
          setSelectedProducts(data.map((p) => p.product_key));
          setNeedsProductSelection(false);
        }
      } catch (error) {
        console.error('Erro ao verificar seleção de produtos:', error);
        // Em caso de erro, assumir que precisa selecionar (mais seguro)
        setNeedsProductSelection(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkProductSelection();

    // Re-verificar quando produtos forem adicionados (ex.: após pagamento confirmado na CheckoutSuccess)
    const handleProductsUpdated = () => {
      setTimeout(() => checkProductSelection(), 300);
    };

    window.addEventListener('organization-created', handleProductsUpdated);
    window.addEventListener('payment-confirmed', handleProductsUpdated);
    window.addEventListener('product-added', handleProductsUpdated);

    return () => {
      window.removeEventListener('organization-created', handleProductsUpdated);
      window.removeEventListener('payment-confirmed', handleProductsUpdated);
      window.removeEventListener('product-added', handleProductsUpdated);
    };
  }, [currentOrg?.id, orgLoading, user]);

  return {
    needsProductSelection,
    isLoading,
    selectedProducts,
  };
}
